import { onMounted, ref } from 'vue';
import moment from 'moment';
import { useI18n } from '@/composables';
import { $emitter } from '@/services/emitter';
import { populateTrackingDomainsStoreCacheApiRequest } from '@/services/api/api-services/domains';
import { getCampaignApiRequest } from '@/services/api/campaign-api';
import { getTrafficSourceApiRequest } from '@/services/api/traffic-sources-api';
import { useStore } from '@/store';

export type CampaignLinksDialogData = {
  id?: string;
};

type TrackingDomain = {
  httpsAvailable?: boolean;
  domain: string;
  nextDnsRecordValidation?: string;
};

type CampaignLinksForm = {
  clickUrl: string;
  multiClickUrl: string;
  campaignRedirectUrl: string;
  campaignRedirectTestUrl: string;
  campaignLandingPixel: string;
  campaignJsRedirect: string;
  campaignImpressionsUrl: string;
  campaignImpressionsPixel: string;
  postbackUrl: string;
  trackingPixelUrl: string;
  trackingPixel: string;
  trackingScript: string;
  campaignImpressionsUrlTxt: string;
  campaignRedirectUrlQR: string;
  campaignRedirectTestUrlQR: string;
};

const defaultForm = (): CampaignLinksForm => ({
  clickUrl: '',
  multiClickUrl: '',
  campaignRedirectUrl: '',
  campaignRedirectTestUrl: '',
  campaignLandingPixel: '',
  campaignJsRedirect: '',
  campaignImpressionsUrl: '',
  campaignImpressionsPixel: '',
  postbackUrl: '',
  trackingPixelUrl: '',
  trackingPixel: '',
  trackingScript: '',
  campaignImpressionsUrlTxt: '',
  campaignRedirectUrlQR: '',
  campaignRedirectTestUrlQR: '',
});

export const useCampaignLinksForm = (
  getData: () => CampaignLinksDialogData,
  emitClose: () => void,
) => {
  const { t } = useI18n();
  const store = useStore();

  const step = ref(1);
  const show = ref(false);
  const trackingDomain = ref<TrackingDomain | null>(null);
  const campaignId = ref('');
  const campaign = ref<Record<string, unknown>>({});
  const form = ref<CampaignLinksForm>(defaultForm());

  const isNextDnsRecordValidationAfterYear = (domain: TrackingDomain | null) => {
    if (!domain?.nextDnsRecordValidation) {
      return false;
    }

    return moment().add(1, 'year').isBefore(domain.nextDnsRecordValidation);
  };

  const close = () => {
    emitClose();
  };

  const openLpPixelDialog = () => {
    $emitter.emit('open-dialog', { name: 'LpPixel', data: { id: campaignId.value } });
  };

  const openTrackConversionsWithNoClickIdsDialog = ({
    linkType,
  }: {
    linkType: string;
  }) => {
    $emitter.emit('open-dialog', {
      name: 'TrackConversionsWithNoClickIds',
      data: {
        campaign: campaign.value,
        linkType,
      },
    });
  };

  const load = async () => {
    const user = store.user as User;

    campaignId.value = getData().id || (store.getTableSelectedItem().id as string);

    let trackingDomainId = user.trackingDomainId;

    store.setLoading(true);

    try {
      const campaignData = await getCampaignApiRequest(campaignId.value);
      campaign.value = campaignData;

      if (campaignData.trackingDomainId) {
        trackingDomainId = campaignData.trackingDomainId;
      }

      trackingDomain.value = store.trackingDomainsObject[trackingDomainId] as TrackingDomain;

      const trafficSourceParams: string[] = [];

      if (campaignData.trafficSourceId) {
        const trafficSource = await getTrafficSourceApiRequest(campaignData.trafficSourceId as string);

        if (trafficSource.costParam) {
          trafficSourceParams.push(
            `${trafficSource.costParam}=${trafficSource.costPlaceholder}`,
          );
        }

        if (trafficSource.extClickIdParam) {
          trafficSourceParams.push(
            `${trafficSource.extClickIdParam}=${trafficSource.extClickIdPlaceholder}`,
          );
        }

        for (let index = 1; index <= 10; index++) {
          const paramKey = `custom${index}Param`;
          const placeholderKey = `custom${index}Placeholder`;
          if (trafficSource[paramKey]) {
            trafficSourceParams.push(
              `${trafficSource[paramKey]}=${trafficSource[placeholderKey]}`,
            );
          }
        }
      }

      const queryString =
        trafficSourceParams.length > 0 ? `?${trafficSourceParams.join('&')}` : '';

      const protocol = trackingDomain.value.httpsAvailable ? 'https' : 'http';
      const domain = trackingDomain.value.domain;
      const campaignUrlId = campaignData.id;

      const jsRedirectHref = `${protocol}://${domain}/go-js/${campaignUrlId}`;
      let jsRedirect = `!function(){var a=document.createElement("script");a.type="text/javascript",a.src="${jsRedirectHref}?"+window.location.search.substring(1);var b=document.getElementsByTagName("script")[0];b.parentNode.insertBefore(a,b)}();`;
      jsRedirect = `<scr` + `ipt type="text/javascript">${jsRedirect}</scr` + `ipt>`;

      const lpPixelHref = `${protocol}://${domain}/landing/${campaignUrlId}?callback=REPLACE&rule=REPLACE&path=REPLACE&landing=REPLACE`;
      let lpPixel = `!function(){var a=document.createElement("script");a.type="text/javascript",a.async=!0,a.src="${lpPixelHref}&"+window.location.search.substring(1);var b=document.getElementsByTagName("script")[0];b.parentNode.insertBefore(a,b)}();`;
      lpPixel = `<scr` + `ipt type="text/javascript">${lpPixel}</scr` + `ipt>`;

      const trackingUrls: CampaignLinksForm = {
        clickUrl: `${protocol}://${domain}/click`,
        multiClickUrl: `${protocol}://${domain}/click/1`,
        campaignRedirectUrl: `https://${domain}/go/${campaignUrlId}${queryString}`,
        campaignRedirectTestUrl: `${protocol}://${domain}/test/go/${campaignUrlId}${queryString}`,
        campaignLandingPixel: lpPixel,
        campaignJsRedirect: jsRedirect,
        campaignImpressionsUrl: `${protocol}://${domain}/impression/${campaignUrlId}${queryString}`,
        campaignImpressionsPixel: `<img src="${protocol}://${domain}/impression/${campaignUrlId}${queryString}" alt="" border="0" width="1" height="1">`,
        postbackUrl: `${protocol}://${domain}/postback?cid=REPLACE&payout=OPTIONAL&txid=OPTIONAL&status=OPTIONAL`,
        trackingPixelUrl: `${protocol}://${domain}/conversion.gif?cid=OPTIONAL&payout=OPTIONAL&txid=OPTIONAL&status=OPTIONAL`,
        trackingPixel: `<img src="${protocol}://${domain}/conversion.gif?cid=OPTIONAL&payout=OPTIONAL&txid=OPTIONAL&status=OPTIONAL" width="1" height="1" border="0" alt="" />`,
        trackingScript: `<scr` + `ipt async type="text/javascript" src="${protocol}://${domain}/conversion.js?cid=OPTIONAL&payout=OPTIONAL&txid=OPTIONAL&status=OPTIONAL"></scr` + `ipt>`,
        campaignImpressionsUrlTxt: `${protocol}://${domain}/conversion.txt?cid=REPLACE&payout=OPTIONAL&txid=OPTIONAL&status=OPTIONAL`,
        campaignRedirectUrlQR: '',
        campaignRedirectTestUrlQR: '',
      };

      trackingUrls.campaignRedirectUrlQR =
        'https://api.qrserver.com/v1/create-qr-code/?size=177x177&margin=10&data=' +
        (trackingUrls.campaignRedirectUrl
          ? encodeURIComponent(String(trackingUrls.campaignRedirectUrl).trim())
          : '');
      trackingUrls.campaignRedirectTestUrlQR =
        'https://api.qrserver.com/v1/create-qr-code/?size=177x177&margin=10&data=' +
        (trackingUrls.campaignRedirectTestUrl
          ? encodeURIComponent(String(trackingUrls.campaignRedirectTestUrl).trim())
          : '');

      form.value = trackingUrls;
      show.value = true;
    } catch {
      $emitter.emit('toast-error', t('Error occurred while dialog opening'));
      close();
    } finally {
      store.setLoading(false);
    }
  };

  const handleEdit = () => {
    close();
    $emitter.emit('open-dialog', { name: 'CampaignDialog', data: { mode: 'edit', id: campaignId.value } });
  };

  onMounted(async () => {
    if (!store.trackingDomains.isUploaded) {
      await populateTrackingDomainsStoreCacheApiRequest();
    }
    await load();
  });

  return {
    t,
    store,
    step,
    show,
    trackingDomain,
    campaign,
    form,
    isNextDnsRecordValidationAfterYear,
    openLpPixelDialog,
    openTrackConversionsWithNoClickIdsDialog,
    handleEdit,
    close,
  };
};
